import { Badge, Tooltip } from '@mantine/core';
import type {
  CreditNoteStatus,
  ImportJobStatus,
  InvoiceStatus,
  ProductionRunStatus,
  PurchaseOrderStatus,
  SalesOrderStatus,
  StockCountStatus,
  XeroConnectionStatus,
  XeroSyncStatus,
} from '@zebra/core';

/**
 * Status pill + Tooltip explaining what the status means, shared by every
 * document lifecycle in the app (guidance wave, 2026-08-25 — generalized out of
 * features/stores-transfers, where it was previously stores-transfers-only).
 *
 * Color follows the branded-neutral semantic ruling (theme.ts, 2026-08-24):
 * amber = draft/pending, brandGreen = posted/success, brandOrange =
 * warning/partial/in-progress, brandRed = blocked/void/error, gray =
 * terminal-neutral (cancelled/reversed — the document no longer counts, but
 * nothing went wrong).
 *
 * The same code means different things in different documents (a PO's `OPEN`
 * is "sent to the supplier, awaiting delivery"; a production run's `OPEN` is
 * "shift in progress") so callers pass a `domain` hint to pick the right
 * sentence. The default domain (`ledgerDoc`) covers the DRAFT/POSTED/REVERSED
 * lifecycle shared by goods receipts, weighbridge tickets, dispatches, stores
 * issues, transfers, stock adjustments, landed cost docs, pack conversions and
 * silo readings (CLAUDE.md: "Posted movements are never updated or deleted;
 * corrections are reversing movements"), plus stock adjustments' extra
 * DRAFT-only CANCELLED state — so existing callers don't have to specify
 * anything.
 */
export type StatusDomain =
  | 'ledgerDoc'
  | 'purchaseOrder'
  | 'salesOrder'
  | 'productionRun'
  | 'stockCount'
  | 'invoice'
  | 'creditNote'
  | 'importJob'
  | 'xeroConnection'
  | 'xeroSync';

interface StatusInfo {
  color: string;
  meaning: string;
}

// Shared by goods receipts, weighbridge tickets, dispatches, stores issues,
// transfers, stock adjustments, landed cost docs, pack conversions and silo
// readings — every one of these uses exactly DRAFT/POSTED/REVERSED, with
// CANCELLED additionally valid for stock adjustments only.
const LEDGER_DOC: Record<string, StatusInfo> = {
  DRAFT: { color: 'amber', meaning: 'Draft — still editable, has not moved any stock yet' },
  POSTED: { color: 'brandGreen', meaning: 'Posted — stock has moved and the document is now locked' },
  REVERSED: { color: 'gray', meaning: 'Reversed — a reversing entry has backed this out of the ledger' },
  CANCELLED: { color: 'gray', meaning: 'Cancelled — discarded before it was ever posted, no stock moved' },
};

const PURCHASE_ORDER: Record<PurchaseOrderStatus, StatusInfo> = {
  DRAFT: { color: 'amber', meaning: 'Draft — not yet sent to the supplier' },
  OPEN: { color: 'brandOrange', meaning: 'Open — sent to the supplier, awaiting delivery' },
  PARTIAL: { color: 'brandOrange', meaning: 'Partially received — some order lines are still outstanding' },
  COMPLETE: { color: 'brandGreen', meaning: 'Complete — everything on the order has been received' },
  CANCELLED: { color: 'gray', meaning: 'Cancelled — this order will not be fulfilled' },
};

const SALES_ORDER: Record<SalesOrderStatus, StatusInfo> = {
  DRAFT: { color: 'amber', meaning: 'Draft — not yet confirmed' },
  OPEN: { color: 'brandOrange', meaning: 'Open — confirmed, awaiting dispatch' },
  COMPLETE: { color: 'brandGreen', meaning: 'Complete — everything on the order has been dispatched' },
  CANCELLED: { color: 'gray', meaning: 'Cancelled — this order will not be fulfilled' },
};

const PRODUCTION_RUN: Record<ProductionRunStatus, StatusInfo> = {
  OPEN: { color: 'brandOrange', meaning: 'Open — the shift is in progress, inputs/outputs can still be edited' },
  CLOSED: { color: 'amber', meaning: 'Closed — entries are locked but not yet posted to the ledger' },
  POSTED: { color: 'brandGreen', meaning: 'Posted — stock has moved and the run is now locked' },
  REVERSED: { color: 'gray', meaning: 'Reversed — a reversing entry has backed this run out of the ledger' },
};

const STOCK_COUNT: Record<StockCountStatus, StatusInfo> = {
  DRAFT: { color: 'amber', meaning: 'Draft — count sheet created, counting has not started' },
  IN_PROGRESS: { color: 'brandOrange', meaning: 'In progress — counted quantities are being entered' },
  COMPLETED: { color: 'brandGreen', meaning: 'Completed — the count has been reconciled against the ledger' },
  CANCELLED: { color: 'gray', meaning: 'Cancelled — this count was abandoned' },
};

const INVOICE: Record<InvoiceStatus, StatusInfo> = {
  DRAFT: { color: 'amber', meaning: 'Draft — not yet approved' },
  APPROVED: { color: 'brandOrange', meaning: 'Approved — finalized, ready to sync to Xero' },
  SYNCED: { color: 'brandGreen', meaning: 'Synced — pushed to Xero' },
  PAID: { color: 'brandGreen', meaning: 'Paid — payment has been received' },
  VOID: { color: 'brandRed', meaning: 'Void — cancelled after approval, no longer valid' },
};

const CREDIT_NOTE: Record<CreditNoteStatus, StatusInfo> = {
  DRAFT: { color: 'amber', meaning: 'Draft — not yet approved' },
  APPROVED: { color: 'brandOrange', meaning: 'Approved — goods restocked (if applicable) and synced' },
  SYNCED: { color: 'brandGreen', meaning: 'Synced — pushed to Xero' },
  VOID: { color: 'brandRed', meaning: 'Void — cancelled after approval, no longer valid' },
};

const IMPORT_JOB: Record<ImportJobStatus, StatusInfo> = {
  DRAFT: { color: 'amber', meaning: 'Draft — file uploaded, not yet validated' },
  VALIDATED: { color: 'brandOrange', meaning: 'Validated — checked for errors, ready to commit' },
  COMMITTED: { color: 'brandGreen', meaning: 'Committed — the data has been imported' },
  FAILED: { color: 'brandRed', meaning: 'Failed — validation or commit hit an error' },
};

const XERO_CONNECTION: Record<XeroConnectionStatus, StatusInfo> = {
  PENDING: { color: 'amber', meaning: 'Pending — connection not yet completed' },
  CONNECTED: { color: 'brandGreen', meaning: 'Connected — linked to Xero' },
  ERROR: { color: 'brandRed', meaning: 'Error — the connection to Xero needs attention' },
};

const XERO_SYNC: Record<XeroSyncStatus, StatusInfo> = {
  PENDING: { color: 'amber', meaning: 'Pending — waiting to sync' },
  SUCCESS: { color: 'brandGreen', meaning: 'Success — synced to Xero' },
  ERROR: { color: 'brandRed', meaning: 'Error — this sync failed' },
};

const STATUS_MAPS: Record<StatusDomain, Record<string, StatusInfo>> = {
  ledgerDoc: LEDGER_DOC,
  purchaseOrder: PURCHASE_ORDER,
  salesOrder: SALES_ORDER,
  productionRun: PRODUCTION_RUN,
  stockCount: STOCK_COUNT,
  invoice: INVOICE,
  creditNote: CREDIT_NOTE,
  importJob: IMPORT_JOB,
  xeroConnection: XERO_CONNECTION,
  xeroSync: XERO_SYNC,
};

export function DocStatusBadge({
  status,
  domain = 'ledgerDoc',
  size,
}: {
  status: string;
  domain?: StatusDomain;
  size?: string;
}) {
  const info = STATUS_MAPS[domain][status];
  return (
    <Tooltip label={info?.meaning ?? status} openDelay={300}>
      <Badge color={info?.color ?? 'gray'} variant="light" size={size}>
        {status.replaceAll('_', ' ')}
      </Badge>
    </Tooltip>
  );
}
